import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Modal, message } from "antd";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { matchPath, useLocation } from "react-router-dom";
import { globalModel } from "../../global/GlobalModel";
import { SpaceApi } from "../../platform_spaces/space/SpaceApi";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { CustomerServiceChatPanel } from "./CustomerServiceChatPanel";
import { CustomerServiceChatModel } from "./CustomerServiceChatModel";
import { CustomerServiceFloatEntry } from "./CustomerServiceEntry";
import { CustomerServiceChatApi } from "./CustomerServiceChatApi";
import {
  CS_AVATAR_URL,
  CS_DISPLAY_NAME,
  CS_SPACE_DISPLAY_NAME,
  type CsChatChannel,
} from "./CustomerServiceChatTypes";
import "./CustomerServiceChatStyle.css";

export type CustomerServiceChatModalsApi = {
  openCustomerServiceChat: () => void;
};

const Ctx = createContext<CustomerServiceChatModalsApi | null>(null);

export function useCustomerServiceChatModal(): CustomerServiceChatModalsApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useCustomerServiceChatModal must be used inside provider");
  }
  return v;
}

function CsModalTitle(props: { avatarUrl?: string; avatarWord: string; label: string }) {
  const { avatarUrl, avatarWord, label } = props;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <EasyAvatar
        shape="square"
        squareRadius={4}
        size={24}
        url={avatarUrl}
        word={avatarWord}
        alt=""
      />
      {label}
    </span>
  );
}

const ModalBody = observer(function ModalBody(props: {
  open: boolean;
  channel: CsChatChannel;
  spaceId?: string;
  displayName: string;
  staffAvatarUrl?: string;
}) {
  const model = useMemo(
    () =>
      new CustomerServiceChatModel({
        channel: props.channel,
        spaceId: props.spaceId,
        displayName: props.displayName,
        staffAvatarUrl: props.staffAvatarUrl,
      }),
    [props.open, props.channel, props.spaceId, props.displayName, props.staffAvatarUrl]
  );
  if (!props.open) return null;
  return <CustomerServiceChatPanel model={model} />;
});

export function CustomerServiceChatModalProvider(props: { children: ReactNode }) {
  const { pathname } = useLocation();
  const spaceMatch = matchPath({ path: "/space/:spaceId/*" }, pathname);
  const currentSpaceId = spaceMatch?.params?.spaceId?.trim() || "";

  const [platformOpen, setPlatformOpen] = useState(false);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [spaceEntryEnabled, setSpaceEntryEnabled] = useState(false);
  const [spaceName, setSpaceName] = useState("");
  const [spaceAvatarUrl, setSpaceAvatarUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!currentSpaceId) {
      setSpaceEntryEnabled(false);
      setSpaceName("");
      setSpaceAvatarUrl(undefined);
      return;
    }
    let cancelled = false;
    void Promise.all([
      CustomerServiceChatApi.getSpaceCsEntryEnabled(currentSpaceId),
      SpaceApi.getSpaceProfile(currentSpaceId),
    ]).then(([enabled, profile]) => {
      if (cancelled) return;
      setSpaceEntryEnabled(enabled);
      const name = profile?.name?.trim() ?? "";
      setSpaceName(name);
      setSpaceAvatarUrl(resolveAppMediaUrl(profile?.avatarUrl));
    });
    return () => {
      cancelled = true;
    };
  }, [currentSpaceId]);

  const spaceCsDisplayName = spaceName || CS_SPACE_DISPLAY_NAME;
  const spaceCsAvatarWord = avatarDisplayWord(spaceName || CS_SPACE_DISPLAY_NAME);

  const openCustomerServiceChat = useCallback(() => {
    if (!globalModel.isLoggedIn) {
      message.info("请先登录后再联系平台客服");
      globalModel.openLoginModal();
      return;
    }
    setPlatformOpen(true);
  }, []);

  const openSpaceCustomerServiceChat = useCallback(() => {
    if (!globalModel.isLoggedIn) {
      message.info("请先登录后再联系空间客服");
      globalModel.openLoginModal();
      return;
    }
    if (!currentSpaceId) return;
    setSpaceOpen(true);
  }, [currentSpaceId]);

  const api = useMemo(() => ({ openCustomerServiceChat }), [openCustomerServiceChat]);

  const showSpaceFloat = Boolean(currentSpaceId) && spaceEntryEnabled;

  return (
    <Ctx.Provider value={api}>
      {props.children}
      {showSpaceFloat ? (
        <CustomerServiceFloatEntry
          onClick={openSpaceCustomerServiceChat}
          title={CS_SPACE_DISPLAY_NAME}
          avatarUrl={spaceAvatarUrl}
          avatarWord={spaceCsAvatarWord}
        />
      ) : null}
      <Modal
        className="lf-cs-modal"
        title={
          <CsModalTitle
            avatarUrl={CS_AVATAR_URL}
            avatarWord={avatarDisplayWord(CS_DISPLAY_NAME)}
            label={CS_DISPLAY_NAME}
          />
        }
        open={platformOpen}
        footer={null}
        width={850}
        destroyOnHidden
        onCancel={() => setPlatformOpen(false)}
      >
        <ModalBody open={platformOpen} channel="PLATFORM" displayName={CS_DISPLAY_NAME} />
      </Modal>
      <Modal
        className="lf-cs-modal"
        title={
          <CsModalTitle
            avatarUrl={spaceAvatarUrl}
            avatarWord={spaceCsAvatarWord}
            label={CS_SPACE_DISPLAY_NAME}
          />
        }
        open={spaceOpen}
        footer={null}
        width={850}
        destroyOnHidden
        onCancel={() => setSpaceOpen(false)}
      >
        <ModalBody
          open={spaceOpen}
          channel="SPACE"
          spaceId={currentSpaceId}
          displayName={spaceCsDisplayName}
          staffAvatarUrl={spaceAvatarUrl}
        />
      </Modal>
    </Ctx.Provider>
  );
}
